import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import UnityWebViewer from "../components/UnityWebViewer";
import { validateTeacherSession } from "../services/teacherSession";
import { getDashboardSessionMessage } from "../utils/customerMessages";

const MAX_REFRESH_DELAY_MS = 2_147_000_000;
const VIEWER_AUTH_MESSAGE = "10k:viewer-auth";
const VIEWER_AUTH_READY_MESSAGE = "10k:viewer-auth-ready";
const DEFAULT_DASHBOARD_ORIGINS = [
  "https://10k.science",
  "https://dashboard.10k.science",
  "https://dashboard-staging.10k.science",
  "http://localhost:3000",
  "http://localhost:3001",
  "http://localhost:3002",
];

const getAllowedDashboardOrigins = () =>
  new Set([
    ...DEFAULT_DASHBOARD_ORIGINS,
    ...(process.env.REACT_APP_DASHBOARD_ORIGINS || "")
      .split(",")
      .map((origin) => origin.trim())
      .filter(Boolean),
  ]);

const EmbedPage = () => {
  const { experienceId } = useParams();
  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;
    const isEmbedded = window.parent !== window;
    const allowedDashboardOrigins = getAllowedDashboardOrigins();

    const requestParentAuth = () => {
      if (!isEmbedded) return;

      for (const origin of allowedDashboardOrigins) {
        window.parent.postMessage(
          {
            type: VIEWER_AUTH_READY_MESSAGE,
            experienceId,
          },
          origin,
        );
      }
    };

    const verifySession = async (token) => {
      try {
        const nextSession = token
          ? await validateTeacherSession({ token })
          : await validateTeacherSession();
        if (cancelled) return;

        setSession(nextSession);
        setSessionError(null);
        clearTimeout(refreshTimer);

        const expiresAt = new Date(nextSession.expiresAt).getTime();
        if (Number.isFinite(expiresAt)) {
          const refreshDelay = Math.min(
            MAX_REFRESH_DELAY_MS,
            Math.max(15_000, expiresAt - Date.now() - 60_000),
          );
          refreshTimer = setTimeout(
            isEmbedded ? requestParentAuth : verifySession,
            refreshDelay,
          );
        }
      } catch (error) {
        if (cancelled) return;
        clearTimeout(refreshTimer);
        setSession(null);
        setSessionError(error);
      }
    };

    const handleAuthMessage = (event) => {
      if (
        event.source !== window.parent ||
        !allowedDashboardOrigins.has(event.origin) ||
        event.data?.type !== VIEWER_AUTH_MESSAGE ||
        event.data?.experienceId !== experienceId ||
        typeof event.data?.token !== "string" ||
        !event.data.token
      ) {
        return;
      }

      void verifySession(event.data.token);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isEmbedded) {
          requestParentAuth();
        } else {
          void verifySession();
        }
      }
    };

    const handleFocus = () => {
      if (isEmbedded) {
        requestParentAuth();
      } else {
        void verifySession();
      }
    };

    window.addEventListener("message", handleAuthMessage);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (isEmbedded) {
      requestParentAuth();
    } else {
      void verifySession();
    }

    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
      window.removeEventListener("message", handleAuthMessage);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [experienceId]);

  if (sessionError) {
    return (
      <div className="App" role="alert">
        <p>{getDashboardSessionMessage(sessionError)}</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="App" role="status">
        Verifying dashboard session...
      </div>
    );
  }

  return (
    <div className="App">
      <UnityWebViewer
        experienceId={experienceId}
        experienceRequestMode="teacher"
        userData={session.userData}
        embed
      />
    </div>
  );
};

export default EmbedPage;
